import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const RESEND_COOLDOWN_SECONDS = 60;

function VerifyOtp() {
    const navigate = useNavigate();
    const location = useLocation();
    const { verifyOtp, resendOtp } = useAuth();

    const [email, setEmail] = useState(location.state?.email || '');
    const [otp, setOtp] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState(location.state?.message || 'Enter the 6-digit OTP sent to your email.');
    const autoSendTriggeredRef = useRef(false);

    const canResend = useMemo(() => cooldown <= 0 && !isResending, [cooldown, isResending]);

    useEffect(() => {
        if (cooldown <= 0) {
            return undefined;
        }

        const timerId = window.setTimeout(() => {
            setCooldown((previous) => Math.max(0, previous - 1));
        }, 1000);

        return () => window.clearTimeout(timerId);
    }, [cooldown]);

    const handleResendOtp = useCallback(async (force = false) => {
        const normalizedEmail = (email || '').trim();

        if (!normalizedEmail) {
            setErrorMessage('Email is required to resend OTP.');
            return;
        }

        if (!force && cooldown > 0) {
            return;
        }

        setErrorMessage('');
        setIsResending(true);

        const resendResult = await resendOtp({ email: normalizedEmail });

        setIsResending(false);

        if (!resendResult.success) {
            setErrorMessage(resendResult.message || 'Unable to resend OTP.');
            return;
        }

        setInfoMessage(resendResult?.data?.message || 'OTP sent again to your email.');
        setCooldown(RESEND_COOLDOWN_SECONDS);
    }, [email, cooldown, resendOtp]);

    useEffect(() => {
        if (!location.state?.autoSendOtp || !(email || '').trim() || autoSendTriggeredRef.current) {
            return;
        }

        autoSendTriggeredRef.current = true;
        handleResendOtp(true);
    }, [location.state?.autoSendOtp, email, handleResendOtp]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage('');

        const normalizedEmail = (email || '').trim();
        const normalizedOtp = (otp || '').trim();

        if (!normalizedEmail) {
            setErrorMessage('Email is required.');
            return;
        }

        if (!/^\d{6}$/.test(normalizedOtp)) {
            setErrorMessage('Please enter a valid 6-digit OTP.');
            return;
        }

        setIsSubmitting(true);

        const result = await verifyOtp({
            email: normalizedEmail,
            otp: normalizedOtp,
        });

        setIsSubmitting(false);

        if (result.success) {
            navigate('/my-account', { replace: true });
            return;
        }

        setErrorMessage(result.message || 'Unable to verify OTP.');
    };

    return (
        <div className="page-content pt-20 pb-20">
            <div className="container">
                <div className="row">
                    <div className="col-xl-8 col-lg-10 col-md-12 m-auto">
                        <div className="row">
                            <div className="col-lg-6 pr-30 d-none d-lg-block">
                                <img className="border-radius-15" src="assets/imgs/page/login-1.png" alt="" />
                            </div>
                            <div className="col-lg-6 col-md-8">
                                <div className="login_wrap widget-taber-content background-white">
                                    <div className="padding_eight_all bg-white">
                                        <div className="heading_s1">
                                            <h1 className="mb-5">Verify OTP</h1>
                                            <p className="mb-30">Enter OTP to complete login. <Link to="/login">Back to Login</Link></p>
                                        </div>
                                        {infoMessage ? <div className="alert alert-info py-2">{infoMessage}</div> : null}
                                        {errorMessage ? <div className="alert alert-danger py-2">{errorMessage}</div> : null}

                                        <form onSubmit={handleSubmit}>
                                            <div className="form-group">
                                                <input
                                                    type="email"
                                                    required
                                                    name="email"
                                                    value={email}
                                                    onChange={(event) => setEmail(event.target.value)}
                                                    placeholder="Email"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <input
                                                    type="text"
                                                    required
                                                    inputMode="numeric"
                                                    pattern="[0-9]{6}"
                                                    maxLength={6}
                                                    name="otp"
                                                    value={otp}
                                                    onChange={(event) => setOtp(event.target.value.replace(/\D/g, ''))}
                                                    placeholder="Enter 6-digit OTP"
                                                />
                                            </div>
                                            <div className="form-group mb-20">
                                                <button
                                                    type="submit"
                                                    className="btn btn-heading btn-block hover-up"
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? 'Verifying...' : 'Verify OTP'}
                                                </button>
                                            </div>
                                        </form>

                                        <div className="form-group mb-10">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-block"
                                                onClick={() => handleResendOtp(false)}
                                                disabled={!canResend}
                                            >
                                                {isResending
                                                    ? 'Sending OTP...'
                                                    : cooldown > 0
                                                        ? `Resend OTP in ${cooldown}s`
                                                        : 'Resend OTP'}
                                            </button>
                                        </div>
                                        <p className="mb-0">New user? <Link to="/register">Register</Link></p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VerifyOtp;
